import { useEffect, useId, useRef, useState } from "react";
import highlightClick from "./highlightClick";
import classes from "./EarTrainerChart.module.css";

const EarTrainerChart = ({ index, image, stringCount, maxFret, onClick,
  feedbackContext, trainingMode, confirmationDuration = 500 }) => {
  const grid = useRef(null);
  const imageRef = useRef(null);
  const cleanupHighlight = useRef(null);
  const id = useId();
  const [location, setLocation] = useState({ string: 0, fret: 0 });
  const string = Math.min(location.string, stringCount - 1);
  const fret = Math.min(location.fret, maxFret - 1);
  const cellId = `${id}-${string}-${fret}`;

  useEffect(() => {
    cleanupHighlight.current?.();
    return () => cleanupHighlight.current?.();
  }, [image, feedbackContext, trainingMode]);

  const submit = (string, fret, shift) => {
    const result = onClick(index, string, fret, shift);
    if (!result) return;
    cleanupHighlight.current?.();
    const rect = imageRef.current.getBoundingClientRect();
    const owner = grid.current.getBoundingClientRect();
    cleanupHighlight.current = highlightClick(
      rect.left - owner.left + (fret + 1) * rect.width / (maxFret + 1),
      rect.top - owner.top + (stringCount - string) * rect.height / (stringCount + 1),
      { container: grid.current, result, size: 32,
        color: result === "correct" ? "green" : result === "incorrect" ? "red" : "#2563eb",
        duration: result === "correct" ? confirmationDuration : 500 });
  };

  const clickHandler = event => {
    const x = event.nativeEvent.offsetX, y = event.nativeEvent.offsetY;
    const fretInc = event.currentTarget.clientWidth / (maxFret + 1);
    const stringInc = event.currentTarget.clientHeight / (stringCount + 1);
    // Keep the original grid-cell hit areas, including unmarked positions.
    const fret = Array.from({ length: maxFret }, (_, n) => n)
      .find(n => x > (n + 0.5) * fretInc && x < (n + 1.5) * fretInc);
    const row = Array.from({ length: stringCount }, (_, n) => n)
      .find(n => y > (n + 0.5) * stringInc && y < (n + 1.5) * stringInc);
    if (fret === undefined || row === undefined) return;
    const string = stringCount - row - 1;
    setLocation({ string, fret });
    grid.current.focus({ preventScroll: true });
    submit(string, fret, event.shiftKey);
  };

  const keyHandler = event => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const moves = {
      ArrowLeft: { string, fret: Math.max(0, fret - 1) },
      ArrowRight: { string, fret: Math.min(maxFret - 1, fret + 1) },
      ArrowUp: { string: Math.min(stringCount - 1, string + 1), fret },
      ArrowDown: { string: Math.max(0, string - 1), fret },
    };
    if (moves[event.key]) {
      event.preventDefault();
      setLocation(moves[event.key]);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!event.repeat) submit(string, fret, event.shiftKey);
    }
  };

  return (
    <div ref={grid} className={classes.chart} tabIndex={0} role="grid"
      aria-label="Practice fretboard" aria-rowcount={stringCount} aria-colcount={maxFret}
      aria-activedescendant={cellId} aria-describedby={`${id}-help ${id}-orientation`} onKeyDown={keyHandler}>
      <img ref={imageRef} id="earTrainerChart" draggable="false" onClick={clickHandler}
        src={`data:image/svg+xml;utf8,${encodeURIComponent(image)}`} alt="" aria-hidden="true" />
      <span className={classes.cursor} aria-hidden="true" style={{
        left: `${(fret + 1) * 100 / (maxFret + 1)}%`,
        top: `${(stringCount - string) * 100 / (stringCount + 1)}%`,
      }} />
      {/* One active cell describes the virtual grid; no note or target names. */}
      <div className={classes.srOnly} role="row" aria-rowindex={stringCount - string}>
        <span role="gridcell" id={cellId} aria-colindex={fret + 1}
          aria-label={`String ${stringCount - string} of ${stringCount}, relative fret ${fret + 1}`} />
      </div>
      <span id={`${id}-help`} className={classes.help}>
        Arrows: move · Enter/Space: select · Shift+Enter/Space: audition
      </span>
      <span id={`${id}-orientation`} className={classes.srOnly}>
        String 1 is the top string; frets are relative to this diagram.
      </span>
    </div>
  );
};

export default EarTrainerChart;
